import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { interpreterService } from "../services/interpreterService";
import { interpreterDisplayId } from "./InterpreterCommonStore";
import { Interpreter } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class InterpreterViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = interpreterService;
  interpreterInstance: Interpreter | null = null;
  cardState: Record<string, boolean> = { systemDates: false };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  createdAt_Value: Date | null = null;
  lastUpdatedAt_Value: Date | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadInterpreterById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.interpreterOne({ id });
    this.interpreterInstance = result.interpreterOne;
    if (this.interpreterInstance) {
      this.loadValues(this.interpreterInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.interpreterInstance) {
      return interpreterDisplayId(this.interpreterInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.InterpreterSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.interpreterInstance = null;
    this.nameValue = "";
    this.createdAt_Value = null;
    this.lastUpdatedAt_Value = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelInterpreter() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("interpreter_update");
  }

  editInterpreter() {
    const id = this.interpreterInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/interpreter/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printInterpreter(handlePrint: () => void): void {
    handlePrint();
  }

  *closeInterpreter(): any {
    yield this.close();
  }

  loadValues(instance: Interpreter) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.createdAt_Value = new Date(instance.createdAt_);
    this.lastUpdatedAt_Value = new Date(instance.lastUpdatedAt_);
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadInterpreterById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
